import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material'
import { useAuth } from '../../contexts/AuthContext'

/**
 * ProtectedRoute component that checks authentication and permissions
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} props.resource - Resource type for permission check
 * @param {string} props.action - Action for permission check (default: 'read')
 * @param {string|Array} props.roles - Required role(s) for access
 */
const ProtectedRoute = ({
  children,
  resource,
  action = 'read',
  roles
}) => {
  const {
    adminUser,
    loading,
    error,
    hasPermission,
    hasRole
  } = useAuth()
  const location = useLocation()

  // Show loading state while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Checking authentication...
        </Typography>
      </Box>
    )
  }

  // Show error state if authentication failed
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
          p: 3
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Button
          variant="contained"
          onClick={() => window.location.href = '/'}
        >
          Return to Home
        </Button>
      </Box>
    )
  }

  // Redirect to login if not authenticated
  if (!adminUser) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check role-based access
  if (roles && !hasRole(roles)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
          p: 3
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            Insufficient Permissions
          </Typography>
          <Typography variant="body2">
            Your role ({adminUser.role}) does not have access to this page.
            {Array.isArray(roles)
              ? ` Required: ${roles.join(' or ')}`
              : ` Required: ${roles}`}
          </Typography>
        </Alert>
        <Button
          variant="contained"
          onClick={() => window.history.back()}
        >
          Go Back
        </Button>
      </Box>
    )
  }

  // Check resource/action permissions
  if (resource && !hasPermission(resource, action)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
          p: 3
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            Permission Denied
          </Typography>
          <Typography variant="body2">
            You do not have permission to {action} {resource}.
          </Typography>
        </Alert>
        <Button
          variant="contained"
          onClick={() => window.history.back()}
        >
          Go Back
        </Button>
      </Box>
    )
  }

  return children
}

export default ProtectedRoute
